/**
 * «Центр уведомлений» (22.10.2026, docs/design/support-2026-10-22).
 *
 * Один лист почти на весь экран: «Уведомления · Чаты», переписка с
 * поддержкой и новое обращение — шагами внутри него, с «назад». Всё из
 * готовых частей приложения: строки — как в «Ещё», переключатель — как
 * «Расходы / Доходы» у категорий, переписка и поле ввода — как в «Спроси
 * SKOKA», форма — чипы, поле и «Прикрепить» из форм.
 */
import { useEffect, useRef, useState } from 'react';
import { Icon } from './icon.js';
import { SheetWrap } from './sheet.js';
import {
  TOPIC_TITLE, imageUrl, loadThread, markNotesRead, sendMessage, shrinkImage, startThread, whenText,
  type ChatMessage, type ChatRow, type Inbox, type Note, type NoteAction, type Topic,
} from './inbox.js';

const NOTE_ICON: Record<Note['kind'], string> = {
  device: 'shield', pro_renew: 'credit-card', pay_failed: 'triangle-exclamation', trial: 'sparkles',
  news: 'circle-info', offer: 'sparkles', feature: 'lightbulb',
};
const TOPIC_ICON: Record<Topic, string> = { question: 'circle-question', bug: 'bug', idea: 'lightbulb', payment: 'credit-card' };
/*
  Состояние — словами человека, а не поддержки (22.10.2026): «ждём ответа»
  читалось так, будто ответа ждёт поддержка.
*/
const STATUS_TEXT: Record<ChatRow['status'], string> = {
  open: 'получено', progress: 'в работе', answered: 'есть ответ', closed: 'закрыто', fixed: 'исправлено',
};
const CH = () => <span className="ch"><Icon name="chevron-right" size={13} /></span>;
const New = () => <> <span className="newtag">новое</span></>;
/** Человечек поддержки — у каждого её сообщения. */
export const SupportAvatar = () => <span className="sup-ava" aria-hidden="true"><Icon name="user-headset" size={17} /></span>;

export type InboxStart = 'notes' | 'chats' | 'new';

export function InboxSheet({ inbox, start = 'notes', onClose, onChanged, onAction, signedIn, onAuth }: {
  inbox: Inbox | null;
  start?: InboxStart;
  onClose: () => void;
  /** Что-то прочитали или отправили — центр перечитать. */
  onChanged: () => void;
  /** Уведомление открывает своё место в приложении. */
  onAction: (action: NoteAction) => void;
  signedIn: boolean;
  onAuth: () => void;
}) {
  const [tab, setTab] = useState<'notes' | 'chats'>(start === 'notes' ? 'notes' : 'chats');
  const [step, setStep] = useState<{ thread: string } | 'new' | null>(start === 'new' ? 'new' : null);
  /*
    «Новое» помечаем на то, что было непрочитанным при открытии: вкладку
    открыли — уведомления прочитаны, но пометки не гаснут у человека на глазах.
  */
  const [fresh] = useState(() => new Set((inbox?.notes ?? []).filter((n) => !n.read).map((n) => n.id)));
  useEffect(() => {
    if (tab !== 'notes' || !inbox) return;
    const ids = inbox.notes.filter((n) => !n.read).map((n) => n.id);
    if (ids.length) void markNotesRead(ids).then(onChanged).catch(() => {});
  }, [tab, inbox]);

  const [threadTitle, setThreadTitle] = useState<string | null>(null);
  const back = step === 'new'
    ? (start === 'new' ? null : () => setStep(null))
    : step ? () => { setStep(null); setThreadTitle(null); onChanged(); } : null;
  const title = step === 'new' ? 'Написать в поддержку'
    : step ? threadTitle ?? inbox?.chats.find((c) => c.id === step.thread)?.title ?? 'Чат'
      : 'Центр уведомлений';

  const notes = inbox?.notes ?? [];
  const chats = inbox?.chats ?? [];
  /*
    Один лист на все шаги: список, чат, новое обращение меняются внутри него,
    с «назад» в шапке. Отдельные листы уезжали бы и поднимались заново.
  */
  return (
    <SheetWrap title={title} onClose={onClose} className="chat inbox"
      {...(back ? { onBack: back, backLabel: 'Назад к чатам' } : {})}>
      {step === 'new' && <NewTicket signedIn={signedIn} onAuth={onAuth}
        onSent={(id) => { onChanged(); setTab('chats'); setStep({ thread: id }); }} />}
      {step && step !== 'new' && <Thread id={step.thread} onTitle={setThreadTitle} />}
      {!step && <>
      <div className="category-tabs wide" role="tablist" aria-label="Что показать">
        <button role="tab" aria-selected={tab === 'notes'} onClick={() => setTab('notes')}>Уведомления</button>
        <button role="tab" aria-selected={tab === 'chats'} onClick={() => setTab('chats')}>
          Чаты{inbox && inbox.unread.chats > 0 && <i className="tabn">{inbox.unread.chats}</i>}
        </button>
      </div>
      <div className="chat-scroll">
        {tab === 'notes' && (notes.length === 0
          ? <p className="inbox-empty">Здесь будут вход с нового устройства, продление Pro, новости и предложения SKOKA.</p>
          : notes.map((n) => {
            const body = <>
              <div className="ico"><Icon name={NOTE_ICON[n.kind] ?? 'circle-info'} size={18} /></div>
              <div className="nm"><b>{n.title}{fresh.has(n.id) && <New />}</b>
                <span>{n.body ? `${n.body} · ` : ''}{whenText(n.at)}</span></div>
            </>;
            // Ведёт куда-то — строка нажимается и со стрелкой; иначе — просто текст.
            return n.action
              ? <button key={n.id} className="li w" onClick={() => onAction(n.action!)}>{body}<CH /></button>
              : <div key={n.id} className="li">{body}</div>;
          }))}
        {tab === 'chats' && (chats.length === 0
          ? <p className="inbox-empty">Напиши, если что-то непонятно, сломалось или есть идея. Ответ придёт сюда и копией на почту.</p>
          : chats.map((c) => (
            <button key={c.id} className="li w" onClick={() => setStep({ thread: c.id })}>
              <div className="ico"><Icon name={TOPIC_ICON[c.topic]} size={18} /></div>
              <div className="nm"><b>{c.title}{c.unread > 0 && <New />}</b>
                <span>№{c.number} · {STATUS_TEXT[c.status]} · {whenText(c.at)}</span></div>
              <CH />
            </button>
          )))}
      </div>
      {tab === 'chats' && <button className="btn pri inbox-write" onClick={() => setStep('new')}>Написать в поддержку</button>}
      </>}
    </SheetWrap>
  );
}

/** Переписка с поддержкой — как «Спроси SKOKA»: свои справа, ответы слева. */
function Thread({ id, onTitle }: { id: string; onTitle: (title: string) => void }) {
  const [data, setData] = useState<{ thread: ChatRow; messages: ChatMessage[] } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [image, setImage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const scroller = useRef<HTMLDivElement>(null);
  const load = () => loadThread(id).then((d) => { setData(d); onTitle(d.thread.title); })
    .catch((e: unknown) => setErr(e instanceof Error ? e.message : 'Не получилось открыть чат'));
  useEffect(() => { void load(); }, [id]);
  // Снимки догружаются после текста — переписка остаётся на последнем сообщении.
  const toBottom = () => { const el = scroller.current; if (el) el.scrollTop = el.scrollHeight; };
  useEffect(toBottom, [data]);
  const ready = (draft.trim().length > 0 || !!image) && !busy;
  const send = async () => {
    if (!ready) return;
    setBusy(true); setErr(null);
    try {
      await sendMessage(id, draft.trim(), image);
      setDraft(''); setImage(null);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Не отправилось — попробуй ещё раз');
    } finally { setBusy(false); }
  };
  return (
    <>
      <div className="chat-scroll" ref={scroller}>
        {!data && !err && <p className="inbox-empty">Открываю чат…</p>}
        {data?.messages.map((m) => (m.author === 'user' ? (
          <div key={m.id} className="chat-row you">
            <div className="chat-you">
              {m.image && <a href={imageUrl(m.id)} target="_blank" rel="noreferrer"><img className="chat-img" onLoad={toBottom} src={imageUrl(m.id)} alt="Снимок экрана" /></a>}
              {m.body}
            </div>
            <span className="chat-stamp">{whenText(m.at)}</span>
          </div>
        ) : (
          <div key={m.id} className="chat-row skoka sup">
            <SupportAvatar />
            <div className="sup-msg">
              {m.image && <a href={imageUrl(m.id)} target="_blank" rel="noreferrer"><img className="chat-img" onLoad={toBottom} src={imageUrl(m.id)} alt="Вложение поддержки" /></a>}
              <p className="chat-text">{m.body}</p>
              <span className="chat-stamp">Поддержка SKOKA{m.auto ? ' · автоответ' : ''} · {whenText(m.at)}</span>
            </div>
          </div>
        )))}
        {err && <div className="note warn" role="alert">{err}</div>}
      </div>
      <div className="chat-compose">
        {image && <div className="chat-attached"><img className="chat-img" src={image} alt="Снимок к сообщению" />
          <button className="lnk" onClick={() => setImage(null)}>Убрать</button></div>}
        <div className="box">
          <label className="icobtn btn file" aria-label="Прикрепить снимок экрана">
            <Icon name="paperclip" size={16} />
            <input type="file" accept="image/*" onChange={(e) => {
              const f = e.target.files?.[0]; e.target.value = '';
              if (f) void shrinkImage(f).then(setImage).catch(() => setErr('Не получилось прикрепить картинку'));
            }} />
          </label>
          <textarea rows={1} value={draft} placeholder="Сообщение" aria-label="Сообщение в поддержку"
            onChange={(e) => setDraft(e.target.value)} />
          <button className={`send${ready ? ' on' : ''}`} disabled={!ready} aria-label="Отправить" onClick={() => void send()}>
            <Icon name="arrow-up" size={18} />
          </button>
        </div>
      </div>
    </>
  );
}

/** Новое обращение: тема, текст, снимок. После «Отправить» открывается чат. */
function NewTicket({ signedIn, onAuth, onSent }: {
  signedIn: boolean; onAuth: () => void; onSent: (id: string) => void;
}) {
  const [topic, setTopic] = useState<Topic>('question');
  const [text, setText] = useState('');
  const [image, setImage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const send = async () => {
    if (busy) return;
    if (!text.trim() && !image) { setErr('Напиши, что случилось'); return; }
    setBusy(true); setErr(null);
    try { onSent(await startThread({ topic, text: text.trim(), image })); } catch (e) {
      setErr(e instanceof Error ? e.message : 'Не отправилось — попробуй ещё раз');
      setBusy(false);
    }
  };
  return (
    <div className="chat-scroll">
      {!signedIn ? (
        <div className="form">
          <div className="note">Чтобы написать в поддержку и получить ответ здесь, войди в аккаунт. Или напиши на почту — ответим туда же.</div>
          <button className="btn pri" onClick={onAuth}>Войти</button>
          <a className="btn" href="mailto:support@skokadeneg.ru">support@skokadeneg.ru</a>
        </div>
      ) : (
        <div className="form">
          <div className="pick"><div className="k">О чём</div>
            <div className="chips">
              {(Object.keys(TOPIC_TITLE) as Topic[]).map((t) => (
                <button key={t} className={`chip${topic === t ? ' on' : ''}`} aria-pressed={topic === t} onClick={() => setTopic(t)}>{TOPIC_TITLE[t]}</button>
              ))}
            </div>
          </div>
          <label>Что случилось
            <textarea rows={4} value={text} placeholder="Что делал и что увидел" maxLength={4000}
              onChange={(e) => setText(e.target.value)} /></label>
          {image
            ? <div className="chat-attached"><img className="chat-img" src={image} alt="Снимок к обращению" />
              <button className="lnk" onClick={() => setImage(null)}>Убрать снимок</button></div>
            : (
              <label className="btn file">
                <Icon name="image" size={16} /> Прикрепить снимок экрана
                <input type="file" accept="image/*" onChange={(e) => {
                  const f = e.target.files?.[0]; e.target.value = '';
                  if (f) void shrinkImage(f).then(setImage).catch(() => setErr('Не получилось прикрепить картинку'));
                }} />
              </label>
            )}
          <div className="note">К сообщению приложим версию приложения и устройство — так разберёмся быстрее. Ответ придёт в «Центр уведомлений → Чаты» и копией на почту.</div>
          {err && <div className="note warn" role="alert">{err}</div>}
          <button className="btn pri" disabled={busy} onClick={() => void send()}>{busy ? 'Отправляю…' : 'Отправить'}</button>
        </div>
      )}
    </div>
  );
}
